'use client';

import { useMergeRefs } from '@floating-ui/react';
import { forwardRef, useContext, useMemo, useRef } from 'react';

import { css, cx } from '@/styled-system/css';

import { useInteractiveElementKeyDownCallback } from '../../hooks/use-interactive-element-key-down-callback';
import { PropertyCardRootContext } from './context/property-card-root.context';
import { propertyCardBodyRecipe } from './styles/property-card-body.styles';
import type { PropertyCardBodyPropsInterface } from './types/property-card-body.types';

/**
 * Used to add card styles and behaviors to the `PropertyCard`. Based on `Card`.
 *
 * @since 8.63.0
 */
export const PropertyCardBody = forwardRef<HTMLDivElement, PropertyCardBodyPropsInterface>(
  (props: PropertyCardBodyPropsInterface, forwardedRef) => {
    const propertyCardRootContext = useContext(PropertyCardRootContext);
    const {
      appearance = propertyCardRootContext.appearance || 'large',
      children,
      className,
      css: cssProp,
      elevated = true,
      onClick,
      onKeyDown,
      interactive = !!onClick,
      role = 'presentation',
      ...rest
    } = props;

    const cardRef = useRef<HTMLDivElement>(null);
    const refs = useMergeRefs([cardRef, forwardedRef]);

    const styles = css(propertyCardBodyRecipe.raw({ appearance, elevated, interactive }), cssProp);

    const onKeyDownCallback = useInteractiveElementKeyDownCallback({
      ref: cardRef,
      onClick,
      onKeyDown,
    });

    const interactiveProps = useMemo(
      () =>
        onClick || onKeyDown
          ? {
              onClick,
              onKeyDown: onKeyDownCallback,
            }
          : {},
      [onClick, onKeyDown, onKeyDownCallback],
    );

    return (
      <div
        ref={refs}
        className={cx('c11n', 'c11n_property-card__body', styles, className)}
        data-c11n-component="PropertyCard.Body"
        data-c11n-version={__C11N_VERSION__}
        role={role}
        {...interactiveProps}
        {...rest}
      >
        {children}
      </div>
    );
  },
);

PropertyCardBody.displayName = 'PropertyCard.Body';
